import {
  Alert, Button, Card, CardContent, Chip, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Typography,
} from '@mui/material';
import { useState } from 'react';
import { executeShellCommandJson } from '../../api/shell';
import { sanitizeShellArg } from '../../utils/shell';

interface PlayerData {
  playerId: string;
  firebaseUid: string;
  displayName: string;
  roles: string[];
  permissions: string[];
  createdAt: string | null;
  lastLoginAt: string | null;
  caseCount: number;
  cases: CaseSummary[];
}

interface CaseSummary {
  caseId: string;
  status: string;
  difficulty: string;
  bootstrapStatus: string;
  createdAt: string | null;
}

const statusColors: Record<string, 'success' | 'warning' | 'error' | 'info' | 'default'> = {
  ACTIVE: 'success',
  READY: 'info',
  CLOSED: 'default',
  ABANDONED: 'error',
};

const bootstrapColors: Record<string, 'success' | 'warning' | 'error' | 'info' | 'default'> = {
  READY: 'success',
  RUNNING: 'info',
  PENDING: 'warning',
  QUEUED: 'warning',
  FAILED: 'error',
};

export function PlayerInspector() {
  const [identifier, setIdentifier] = useState('');
  const [player, setPlayer] = useState<PlayerData | null>(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  async function handleLookup() {
    if (!identifier.trim()) return;
    setLoading(true);
    setError('');
    setPlayer(null);
    try {
      const res = await executeShellCommandJson(`player.get ${sanitizeShellArg(identifier)}`);
      setPlayer(res as unknown as PlayerData);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Request failed');
    } finally {
      setLoading(false);
    }
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleLookup();
    }
  }

  return (
    <Stack spacing={3}>
      <Card sx={{ bgcolor: '#1a1d22' }}>
        <CardContent>
          <Stack spacing={2}>
            <Typography variant="h6">Player Lookup</Typography>
            <Stack direction="row" spacing={2}>
              <TextField
                label="Player ID or Firebase UID"
                value={identifier}
                onChange={(e) => setIdentifier(e.target.value)}
                onKeyDown={handleKeyDown}
                fullWidth
                size="small"
                placeholder="UUID or Firebase UID"
              />
              <Button variant="contained" onClick={handleLookup} disabled={loading}>
                Lookup
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      {player && (
        <>
          <Card sx={{ bgcolor: '#1a1d22' }}>
            <CardContent>
              <Typography variant="subtitle2" sx={{ mb: 2 }}>Player Profile</Typography>
              <Table size="small">
                <TableBody>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold', width: 160 }}>Player ID</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{player.playerId}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Firebase UID</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{player.firebaseUid}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Display Name</TableCell>
                    <TableCell>{player.displayName}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Roles</TableCell>
                    <TableCell>
                      {player.roles.length > 0
                        ? player.roles.map((role) => (
                            <Chip key={role} label={role} size="small" variant="outlined" sx={{ mr: 0.5 }} />
                          ))
                        : '—'}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Permissions</TableCell>
                    <TableCell>
                      {player.permissions.length > 0
                        ? player.permissions.map((p) => (
                            <Chip key={p} label={p} size="small" variant="outlined" sx={{ mr: 0.5 }} />
                          ))
                        : '—'}
                    </TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Created</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{formatTime(player.createdAt)}</TableCell>
                  </TableRow>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 'bold' }}>Last Login</TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>{formatTime(player.lastLoginAt)}</TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          <Card sx={{ bgcolor: '#1a1d22' }}>
            <CardContent>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>
                Cases ({player.caseCount})
              </Typography>
              {player.cases.length > 0 ? (
                <TableContainer sx={{ maxHeight: 400 }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 'bold' }}>Case ID</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Status</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Difficulty</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Bootstrap</TableCell>
                        <TableCell sx={{ fontWeight: 'bold' }}>Created</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {player.cases.map((c) => (
                        <TableRow key={c.caseId} hover>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                            {c.caseId}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={c.status}
                              size="small"
                              color={statusColors[c.status] ?? 'default'}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell>{c.difficulty}</TableCell>
                          <TableCell>
                            <Chip
                              label={c.bootstrapStatus}
                              size="small"
                              color={bootstrapColors[c.bootstrapStatus] ?? 'default'}
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem', whiteSpace: 'nowrap' }}>
                            {formatTime(c.createdAt)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Typography variant="body2" color="text.secondary">No cases found.</Typography>
              )}
            </CardContent>
          </Card>
        </>
      )}

      {error && <Alert severity="error">{error}</Alert>}
    </Stack>
  );
}

function formatTime(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}
